import React, { Suspense } from 'react'
import intl from 'react-intl-universal'
import Lightbox from 'react-image-lightbox'
import { useQuery } from '@apollo/client'
import { uploadFile, IMAGE_MIMETYPES, getSignedValue } from 'actions/files'
import { AppState, getAppStateQuery } from 'graphql/queries/app/getAppState'
import { showModal } from 'components/utils/CustomModal'
import { MiniLoader } from './MiniLoader'
import { showSave } from './SavingIndicator'
import 'react-image-lightbox/style.css'
import Video from '../action/types/Video'
import { GetOrganizationsRes, GetOrganizationsVars, getOrganizationsQuery } from 'graphql/queries/organization/getOrganziations'
import { useActiveWorkspaceId } from 'hooks/useActiveWorkspaceId'
import { Button } from './Button'
import { UpsellModal } from 'components/modals/UpsellModal'
import { GetAddonLimitPerOrganizationRes, GetAddonLimitPerOrganizationVars } from 'graphql/queries/course/getTranslatorLimitPerOrganization'
import { getGnowbeAiLimitPerOrganizationQuery } from 'graphql/queries/course/getGnowbeAiLimitPerOrganization'

type CoverEditableProps = {
  disabled: boolean;
  token: string;
  target: string;
  defaultImageUrl: string;
  defaultVideoUrl?: string;
  videoUrl_720p?: string;
  sessionVideo?: boolean;
  field: string;
  allowMediaDownload: boolean;
  isEditing: boolean;
  mimetypes?: string[];
  fullWidth?: boolean;
  update: (f: string, v: string) => void;
  courseId: string;
  showAiGenerateImage?: boolean;
  organizationId?: string;
  limitExceeded?: boolean;
}

type CoverEditableState = {
  value: string;
  hdVideoValue: string;
  isUploading: boolean;
  zoomed: boolean;
  realSize?: { width: number, height: number };
}

const Loading = () => {
  return (
    <div className="mt-4 py-7 text-center">
      Please wait while our Gnome loads the page...
    </div>
  )
}

class Editable extends React.PureComponent<CoverEditableProps, CoverEditableState> {
  private _cover
  private _isMounted = false

  constructor(props) {
    super(props)
    this.state = { value: props.defaultImageUrl || props.defaultVideoUrl || '', hdVideoValue: props.videoUrl_720p || '', isUploading: false, zoomed: false }
    this.handleRightClick = this.handleRightClick.bind(this)
    this.getImageSize = this.getImageSize.bind(this)
    this.toggleZoom = this.toggleZoom.bind(this)
  }

  componentDidMount() {
    this._isMounted = true
    this.getImageSize()
    this._cover && this._cover.addEventListener('contextmenu', this.handleRightClick)
  }

  componentDidUpdate(prevProps) {
    if (prevProps.defaultImageUrl === this.props.defaultImageUrl) return
    if (prevProps.defaultImageUrl !== this.props.defaultImageUrl || prevProps.defaultVideoUrl !== this.props.defaultVideoUrl) {
      this.setState({ value: this.props.defaultImageUrl || this.props.defaultVideoUrl || '', hdVideoValue: this.props.videoUrl_720p || '' })
    }
    this.getImageSize()
  }

  componentWillUnmount() {
    this._isMounted = false
    this._cover && this._cover.removeEventListener('contextmenu', this.handleRightClick)
  }

  getImageSize(setState: boolean = true): { width: number, height: number } {
    const img = new Image()
    img.onload = () => {
      const height = img.height
      const width = img.width
      this._isMounted && setState && this.setState({ realSize: { height, width } })
      return { height, width }
    }
    img.src = this.props.defaultImageUrl
    return { width: 0, height: 0 }
  }

  handleRightClick(e: React.MouseEvent) {
    if (this.props.allowMediaDownload || this.props.isEditing) {
      return
    }
    e.preventDefault()
    return false
  }

  handlePhotoUpload(e: React.SyntheticEvent<any>, field: string) {
    if (!this.state.isUploading && !this.props.disabled) {
      this._isMounted && this.setState({ isUploading: true })
      uploadFile({ authToken: this.props.token, mimetypes: this.props.mimetypes || IMAGE_MIMETYPES, target: this.props.target, courseId: this.props.courseId, canGenerateAiImages: this.props.showAiGenerateImage, organizationId: this.props.organizationId, limitExceeded: this.props.limitExceeded })
        .then((data) => {
          showSave()
          const signedData = data.url ? getSignedValue(data.url) : ''
          this._isMounted && this.setState({ isUploading: false, value: signedData })
          if (data?.type?.match(/video/)) {
            this.props.update('videoUrl', data.url || '')
          }
          else {
            this.props.update(field, data.url || '')
          }
        })
        .catch((err) => {
          this._isMounted && this.setState({ isUploading: false })
        })
    }
  }

  handlePhotoDelete(e: React.SyntheticEvent<any>, field: string) {
    showModal({
      title: intl.get('global_are_you_sure_header'),
      content: !this.props.defaultVideoUrl ? intl.get('confirm_photo_delete_text') : intl.get('confirm_video_delete_text'),
      primaryText: !this.props.defaultVideoUrl ? intl.get('remove_photo_button') : 'Remove Video',
      primaryAction: () => {
        this.setState({ value: '' })
        this.props.update(field, '')
      },
      destructive: true,
    })
  }

  toggleZoom(e) {
    this._isMounted && this.setState({ zoomed: !this.state.zoomed })
  }

  render() {
    const { isEditing, field, disabled } = this.props
    const { value, hdVideoValue, isUploading } = this.state

    const signedValue = getSignedValue(value)
    const signedValue720 = getSignedValue(hdVideoValue)

    if (!isEditing) {
      return (
        <div className="flex justify-center mb-8">
          <div className={`image-holder ${!this.props.sessionVideo && 'max-h-[300px]'} ${signedValue && this.props.defaultVideoUrl ? 'video' : ''}`}>
            {signedValue
              ? this.props.defaultImageUrl
                ? <img
                    ref={ref => this._cover = ref}
                    className="image"
                    src={signedValue}
                    alt="Cover image"
                    onClick={() => this.setState({ zoomed: true })}
                  />
                : <Suspense fallback={<Loading />}>
                    <Video
                      videoUrl={signedValue}
                      hdVideoUrl={signedValue720}
                      allowMediaDownload={false}
                    />
                  </Suspense>
              : <div className="image" />
            }
          </div>

          {this.state.zoomed && (
            <Lightbox
              mainSrc={signedValue}
              onCloseRequest={() => this.setState({ zoomed: false })}
            />
          )}
        </div>
      )
    }

    return (
      <div>
        <div className="flex justify-center">
          <div className={`image-holder ${value && this.props.defaultVideoUrl ? 'video' : ''}`}>
            {isUploading
              ? <div className="flex w-[400px] h-[300px] items-center justify-center border border-team rounded-sm">
                  <MiniLoader />
                </div>
              : signedValue
                ? this.props.defaultImageUrl
                  ? 
                  <img
                      className="image editable border-solid"
                      src={signedValue ? signedValue : '/images/image-placeholder.jpg'}
                      onClick={e => this.handlePhotoUpload(e, field)}
                      alt="Upload photo or video"
                    />
                  : <div className="image editable border-solid h-full">
                    <Suspense fallback={<Loading />}>
                      <Video
                        videoUrl={signedValue}
                        hdVideoUrl={signedValue720}
                        allowMediaDownload={false}
                        sessionEditVideo={true}
                      />
                    </Suspense>
                    </div>
                : <img
                    className="image editable border-solid" data-test="upload-img-btn"
                    src="/images/image-placeholder.jpg"
                    onClick={e => this.handlePhotoUpload(e, field)}
                    alt="Upload photo or video"
                  />
            }

            {value && !disabled && !isUploading && (
              <button
                className="absolute right-0 bottom-0 mr-4 mb-4 icon icon-trash-alt-light w-10 h-10 rounded-full bg-coral hover:bg-red-800 text-white"
                onClick={(e) => {
                  e.stopPropagation()
                  this.handlePhotoDelete(e, this.props.defaultImageUrl ? 'imageUrl' : 'videoUrl')
                }}
                aria-label={`Remove ${this.props.defaultImageUrl ? 'photo' : 'video'}`}
              />
            )}
            {/* {(this.props.showAiGenerateImage && this.props.limitExceeded) && <div>
              <Button className={`absolute ${value && !disabled && !isUploading ? 'right-16' : 'right-4'} bottom-4 bg-[#ED8054] text-white rounded-3xl px-0 w-10 h-10 border-none focus:ring-[#ED8054]`} 
                svgIcon={<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="16" cy="16" r="15.5" fill="#ED8054" stroke="#ED8054"/>
                    <g clipPath="url(#clip0_0_1000)">
                      <path d="M15.6142 14.0279C15.7379 13.6566 16.2622 13.6566 16.3859 14.0279L17.1115 16.207C17.2712 16.686 17.5403 17.1213 17.8975 17.4782C18.2547 17.8352 18.69 18.104 19.1692 18.2635L21.3472 18.9891C21.7184 19.1129 21.7184 19.6371 21.3472 19.7609L19.168 20.4865C18.689 20.6462 18.2538 20.9153 17.8968 21.2725C17.5398 21.6296 17.271 22.065 17.1115 22.5441L16.3859 24.7221C16.3592 24.8034 16.3076 24.8742 16.2383 24.9244C16.169 24.9746 16.0856 25.0016 16 25.0016C15.9145 25.0016 15.8311 24.9746 15.7618 24.9244C15.6925 24.8742 15.6408 24.8034 15.6142 24.7221L14.8885 22.543C14.7289 22.0641 14.46 21.6289 14.103 21.272C13.7461 20.915 13.3109 20.6461 12.832 20.4865L10.6529 19.7609C10.5716 19.7342 10.5008 19.6825 10.4506 19.6132C10.4004 19.5439 10.3734 19.4606 10.3734 19.375C10.3734 19.2894 10.4004 19.2061 10.4506 19.1368C10.5008 19.0675 10.5716 19.0158 10.6529 18.9891L12.832 18.2635C13.3109 18.1039 13.7461 17.835 14.103 17.478C14.46 17.1211 14.7289 16.6859 14.8885 16.207L15.6142 14.0279ZM11.2683 8.29149C11.2844 8.24277 11.3155 8.20038 11.3571 8.17033C11.3987 8.14028 11.4487 8.12411 11.5 8.12411C11.5514 8.12411 11.6014 8.14028 11.643 8.17033C11.6846 8.20038 11.7156 8.24277 11.7318 8.29149L12.1672 9.59874C12.3618 10.1815 12.8185 10.6382 13.4013 10.8329L14.7085 11.2682C14.7573 11.2844 14.7996 11.3155 14.8297 11.3571C14.8597 11.3987 14.8759 11.4487 14.8759 11.5C14.8759 11.5513 14.8597 11.6013 14.8297 11.6429C14.7996 11.6845 14.7573 11.7156 14.7085 11.7317L13.4013 12.1671C13.1139 12.2629 12.8527 12.4243 12.6385 12.6385C12.4243 12.8527 12.2629 13.1138 12.1672 13.4012L11.7318 14.7085C11.7156 14.7572 11.6846 14.7996 11.643 14.8297C11.6014 14.8597 11.5514 14.8759 11.5 14.8759C11.4487 14.8759 11.3987 14.8597 11.3571 14.8297C11.3155 14.7996 11.2844 14.7572 11.2683 14.7085L10.8329 13.4012C10.7371 13.1138 10.5758 12.8527 10.3616 12.6385C10.1473 12.4243 9.88619 12.2629 9.59878 12.1671L8.29153 11.7317C8.24281 11.7156 8.20042 11.6845 8.17037 11.6429C8.14032 11.6013 8.12415 11.5513 8.12415 11.5C8.12415 11.4487 8.14032 11.3987 8.17037 11.3571C8.20042 11.3155 8.24281 11.2844 8.29153 11.2682L9.59878 10.8329C9.88619 10.7371 10.1473 10.5757 10.3616 10.3615C10.5758 10.1473 10.7371 9.88615 10.8329 9.59874L11.2683 8.29149ZM19.2209 7.11194C19.2209 7.11156 19.221 7.11119 19.2211 7.11083C19.2322 7.07902 19.253 7.05145 19.2804 7.0319C19.3081 7.01224 19.3411 7.00168 19.375 7.00168C19.4089 7.00168 19.442 7.01224 19.4696 7.0319C19.4972 7.05156 19.5181 7.07934 19.5292 7.11137L19.8194 7.98212C19.9488 8.37137 20.2537 8.67624 20.6429 8.80562L21.5137 9.09587C21.5457 9.10697 21.5735 9.12778 21.5931 9.15541C21.6128 9.18303 21.6233 9.21609 21.6233 9.24999C21.6233 9.2839 21.6128 9.31696 21.5931 9.34458C21.5735 9.3722 21.5457 9.39301 21.5137 9.40412L20.6429 9.69437C20.4511 9.75818 20.2768 9.86584 20.1338 10.0088C19.9909 10.1517 19.8832 10.326 19.8194 10.5179L19.5292 11.3886C19.5181 11.4207 19.4972 11.4484 19.4696 11.4681C19.442 11.4877 19.4089 11.4983 19.375 11.4983C19.3411 11.4983 19.3081 11.4877 19.2804 11.4681C19.2528 11.4484 19.232 11.4207 19.2209 11.3886L18.9307 10.5179C18.8668 10.326 18.7592 10.1517 18.6162 10.0088C18.4733 9.86584 18.299 9.75818 18.1072 9.69437L17.2375 9.40412C17.2055 9.39301 17.1777 9.3722 17.1581 9.34458C17.1384 9.31696 17.1278 9.2839 17.1278 9.24999C17.1278 9.21609 17.1384 9.18303 17.1581 9.15541C17.1777 9.12778 17.2055 9.10697 17.2375 9.09587L18.1083 8.80562C18.4975 8.67624 18.8024 8.37137 18.9318 7.98212L19.2207 7.11302C19.2208 7.11267 19.2209 7.11231 19.2209 7.11194Z" fill="white"/>
                    </g>
                    <defs>
                      <clipPath id="clip0_0_1000">
                        <rect width="18" height="18" fill="white" transform="translate(7 7)"/>
                      </clipPath>
                    </defs>
                  </svg>}
                text={''}
                onClick={() => {
                  const modal = showModal({
                    title: '',
                    component: (
                      <UpsellModal
                        track="gnowbe-ai"
                        type="gnowbe-ai"
                        hideFeatures={true}
                        close={() => modal.close()}
                      />
                    ),
                    onlyContent: true,
                  })
                  return
                }} />
            </div>} */}
          </div>
        </div>

        <div className="text-center text-sm my-6">
          {intl.getHTML('action_note_photo_ext')}
        </div>
      </div>
    )
  }
}

export const CoverEditable = (p) => {
  const activeWorkspaceId = useActiveWorkspaceId()
  const { data: appData } = useQuery<AppState>(getAppStateQuery)
  const { data: orgData, loading: orgLoading, refetch } = useQuery<GetOrganizationsRes, GetOrganizationsVars>(getOrganizationsQuery, {
    skip: !appData,
    variables: {
      searchText: '',
      skip: 0,
      limit: 100,
      limitToUserOrganizations: appData?.appState.loggedInAs.uid || '',
      limitToManagerOrganizations: appData?.appState.loggedInAs.uid || '',
      disabled: false,
    },
    errorPolicy: 'all',
  })

  const { data: gnowbeAiUsage } = useQuery<GetAddonLimitPerOrganizationRes, GetAddonLimitPerOrganizationVars>(getGnowbeAiLimitPerOrganizationQuery, {
    variables: { organizationId: activeWorkspaceId },
  })
  const count = gnowbeAiUsage?.res.count || 0
  const limit = gnowbeAiUsage?.res.limit || 0

  const workspaceCanGenerateImage = orgData?.res.organizations.find(o => o.id === activeWorkspaceId)?.featureFlags.includes('generateImageAi')
  const limitExceeded = count >= limit

  if (!appData) return <MiniLoader />

  return (
    <Editable
      disabled={false}
      target={p.target}
      defaultImageUrl={p.defaultImageUrl}
      defaultVideoUrl={p.defaultVideoUrl}
      sessionVideo={!!p.defaultVideoUrl}
      videoUrl_720p={p.videoUrl_720p}
      field={p.field}
      allowMediaDownload={p.allowMediaDownload}
      isEditing={p.isEditing}
      mimetypes={p.mimetypes}
      token={appData.appState.loggedInAs.token}
      fullWidth={p.fullWidth}
      update={p.update}
      courseId={appData.appState.currentCompanyIdCourseId.split('-')[1]}
      showAiGenerateImage={workspaceCanGenerateImage}
      organizationId={activeWorkspaceId}
      limitExceeded={limitExceeded}
    />
  )
}
